import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { KIND_LABEL, useMessageMutation, useMessages, type GuestMessage, type MessageList, type MessageStatus } from '../api/messages';
import { formatDateTime, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

const STATUS: Record<MessageStatus, { label: string; tone: Tone }> = {
  QUEUED: { label: 'Waiting to send', tone: 'info' },
  SENT: { label: 'Sent', tone: 'good' },
  FAILED: { label: 'Did not go', tone: 'bad' },
  SKIPPED: { label: 'Not sent', tone: 'warn' },
};
const CHANNEL = { SMS: 'Text', EMAIL: 'Email', WHATSAPP: 'WhatsApp' } as const;

/** Says plainly when messages are not really reaching guests, so no one assumes a guest was told when they were not. */
function ProviderNote({ data }: { data: MessageList }) {
  if (data.provider === 'none') return <p className="site-error" role="alert" style={{ margin: 0 }}>No message provider is set up, so guests are <strong>not</strong> being messaged. Phone them instead. Messages below are recorded as not sent.</p>;
  if (data.provider === 'fake') return <p className="hint" style={{ margin: 0 }}>Test mode: messages are recorded but not really sent.</p>;
  return null;
}

function Row({ m, showBooking }: { m: GuestMessage; showBooking?: boolean }) {
  const { can } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [open, setOpen] = useState(false);
  const retry = useMessageMutation(() => api(`/messages/${m.id}/retry`, { method: 'POST', body: {} }));
  return (
    <div className="item">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="title">{KIND_LABEL[m.kind] ?? m.kind}</span>
        <span className="row">
          <Badge>{CHANNEL[m.channel]}</Badge>
          <Badge tone={STATUS[m.status].tone}>{STATUS[m.status].label}</Badge>
        </span>
      </div>
      <div className="small muted">
        {m.to || 'no address'} · {formatDateTime(m.sentAt ?? m.createdAt)} · {m.by}
        {m.segments && m.segments > 1 ? ` · ${m.segments} text parts` : ''}
        {m.attempts > 1 ? ` · ${m.attempts} tries` : ''}
      </div>
      {m.error && m.status !== 'SENT' ? <div className="hint">{m.error}</div> : null}
      {open ? <p className="small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{m.body}</p> : null}
      <div className="row">
        <Button variant="ghost" onClick={() => setOpen(!open)}>{open ? 'Hide text' : 'Show text'}</Button>
        {showBooking && m.bookingId ? <Link className="btn" to={`/bookings/${m.bookingId}`}>Open booking</Link> : null}
        {(m.status === 'FAILED' || m.status === 'SKIPPED') && can('message.send') ? (
          <Button loading={retry.isPending} onClick={() => retry.mutate(undefined, { onError: report, onSuccess: () => toast.ok('Queued to send again') })}>Send again</Button>
        ) : null}
      </div>
    </div>
  );
}

export function Messages() {
  const [tab, setTab] = useState<'attention' | 'all'>('attention');
  const all = useMessages();
  const rows = (all.data?.messages ?? []).filter((m) => tab === 'all' || m.status === 'FAILED' || m.status === 'SKIPPED' || m.status === 'QUEUED');
  return (
    <div className="page">
      <PageHead eyebrow="Guests" title="Messages" />
      {all.data ? <ProviderNote data={all.data} /> : null}
      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'attention'} onClick={() => setTab('attention')}>Needs attention{all.data && all.data.trouble > 0 ? ` (${all.data.trouble})` : ''}</button>
        <button type="button" aria-pressed={tab === 'all'} onClick={() => setTab('all')}>Everything</button>
      </div>
      {all.isLoading ? (
        <Loading rows={3} />
      ) : all.error ? (
        <ErrorNote error={all.error} retry={() => void all.refetch()} />
      ) : rows.length === 0 ? (
        <div className="card flat"><Empty icon="inbox" title={tab === 'attention' ? 'Every message has gone' : 'No messages yet'}>{tab === 'attention' ? 'Anything that did not reach a guest shows here, with the reason, so you can call them or send it again.' : 'Guests are messaged when a booking request arrives, is confirmed or cancelled, and the day before they arrive.'}</Empty></div>
      ) : (
        <div className="list">{rows.map((m) => <Row key={m.id} m={m} showBooking />)}</div>
      )}
    </div>
  );
}

/** The messages for one booking, with a way to write to the guest. Shown on the booking page. */
export function BookingMessages({ bookingId }: { bookingId: string }) {
  const { can } = useAuth();
  const q = useMessages({ bookingId });
  const [writing, setWriting] = useState(false);
  return (
    <section className="card" aria-labelledby="msgs">
      <div className="card-head">
        <h2 className="h2" id="msgs">Messages to the guest</h2>
        {can('message.send') ? <Button onClick={() => setWriting(true)}>Write to the guest</Button> : null}
      </div>
      {q.data ? <ProviderNote data={q.data} /> : null}
      {q.isLoading ? (
        <Loading rows={1} />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data?.messages ?? []).length === 0 ? (
        <p className="muted small">Nothing has been sent to this guest yet.</p>
      ) : (
        <div className="list">{q.data!.messages.map((m) => <Row key={m.id} m={m} />)}</div>
      )}
      {writing ? <WriteDialog bookingId={bookingId} onClose={() => setWriting(false)} /> : null}
    </section>
  );
}

function WriteDialog({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const [text, setText] = useState('');
  const send = useMessageMutation(() => api(`/bookings/${bookingId}/messages`, { method: 'POST', body: { text: text.trim() } }));
  return (
    <Modal open onClose={onClose} title="Write to the guest">
      <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (text.trim().length >= 2) send.mutate(undefined, { onError: report, onSuccess: () => { toast.ok('Message queued'); onClose(); } }); }}>
        <Field id="wm" label="Message" hint="Sent as a text and, if the guest has an email, as an email. Keep it short.">
          <textarea id="wm" className="textarea" rows={4} maxLength={600} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={text.trim().length < 2} loading={send.isPending}>Send</Button>
        </div>
      </form>
    </Modal>
  );
}
